import { DataSnapshot } from 'firebase/database';

import { DataBaseNodes, PatientSites } from '@lib/firebase';
import { usePatientStore, useSiteStore } from '@stores';
import { Patient, PatientConnectionStatus } from '@type';
import { getDateNow, isDefinedAndNotEmptyObject } from '@utils';
import ProfileDefaultPic from '@assets/images/default_profile_pic.png';

import { Encryption } from '../../encryption-service';
import {
  CommunicationKeys,
  DecryptedPatientSettings,
  EncryptedPatientSettings,
  PatientProfileResponse,
  PatientSettingsPayload,
  Preferences,
} from '../types';
import { BaseGateway } from './base.gateway';

export class PatientGateway extends BaseGateway {
  async getPatientSites() {
    return await this.sourceService.readDataOnce<PatientSites>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.SiteList}`
    );
  }

  subscribeToPatientSites(callback: (sites: PatientSites) => void) {
    return this.sourceService.subscribeToChild(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.SiteList}`,
      (snapshot: DataSnapshot) => {
        if (!snapshot.exists()) return [];
        const data = snapshot.val() as PatientSites;
        callback(data);
      }
    );
  }

  async getIsPatientDeleted() {
    return await this.sourceService.readDataOnce<boolean>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.IsDeleted}`
    );
  }

  async getPatientProfileData() {
    const patientProfile = await this.sourceService.readDataOnce<{
      document: string;
      siteUUID: string;
    }>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.PatientProfile}`
    );
    if (!patientProfile || !isDefinedAndNotEmptyObject(patientProfile)) return;

    const { updatePatientInfo } = usePatientStore.getState();
    const { getSiteByUUID } = useSiteStore.getState();

    const { document: encryptedProfileData, siteUUID } = patientProfile;
    const site = getSiteByUUID(siteUUID);
    const profileData = Encryption.decryptAndParseObject<
      Partial<PatientProfileResponse>
    >(encryptedProfileData, site.sharedSecretKey);
    const formattedData = {
      ...profileData,
      profilePicture: profileData?.profilePicture ?? ProfileDefaultPic,
      insuranceType: profileData?.InsuranceType,
      insuranceCompany: profileData?.InsuranceCompany,
      insuranceNumber: profileData?.InsuranceNumber,
      firstname: profileData?.firstname?.trim(),
      lastname: profileData?.lastname?.trim(),
    };
    if (!profileData) return;
    updatePatientInfo(formattedData);
  }

  async getInsuranceTypes() {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { hashedSharedKey, siteUuid } = getSiteByProjectId(
      this.config.projectId!
    );
    const payload = {
      preference: Preferences.Profile.GetInsuranceTypes,
      hashedSharedKey,
    };
    return await this.dataExchange({
      siteUuid,
      payload,
      successKey: CommunicationKeys.InsuranceTypes.Success,
      failureKey: CommunicationKeys.InsuranceTypes.Failed,
    }).catch(error => {
      throw new Error(
        `Error with getting insurance types: ${JSON.stringify(error)}`
      );
    });
  }

  async updatePatientConnectionStatus(status: PatientConnectionStatus) {
    return await this.sourceService.updateData<{
      isLoggedIn: boolean;
      status: PatientConnectionStatus;
      timestamp: Date;
    }>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.Connection}`,
      {
        isLoggedIn: status !== PatientConnectionStatus.Unpaired,
        status,
        timestamp: getDateNow().toDate(),
      }
    );
  }

  async uploadPatientProfilePicture(patientProfilePicture: File) {
    return await this.sourceService.uploadFile(
      patientProfilePicture,
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.Files}/`
    );
  }

  async updatePatientProfileData(pateintProfileData: Partial<Patient>) {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { hashedSharedKey, sharedSecretKey, siteUuid } = getSiteByProjectId(
      this.config.projectId!
    );
    const encryptedData = Encryption.encrypt(
      {
        ...pateintProfileData,
        isValidPatient: true,
      },
      sharedSecretKey
    );
    const payload = {
      preference: Preferences.DummyPatient.FillDummyPatientData,
      encryptedData,
      hashedSharedKey,
    };
    return await this.dataExchange({
      siteUuid,
      payload,
      successKey: CommunicationKeys.DummyPatient.UpdateSuccess,
      failureKey: CommunicationKeys.DummyPatient.UpdateFailed,
    }).catch(error => {
      throw new Error(
        `Error with updating Patient Profile data: ${JSON.stringify(error)}`
      );
    });
  }

  async getPatientInstances() {
    const sites = await this.sourceService.readDataOnce<PatientSites>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.SiteList}`
    );
    const instances: Record<string, string> = {};
    for (const site in sites) {
      instances[`${sites[site].firebaseProjectId}`] =
        sites[site].firebasePatientID;
    }
    return instances;
  }

  async getPatientSettings() {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { siteUuid, sharedSecretKey, siteId } = getSiteByProjectId(
      this.config.projectId as string
    );
    const payload = {
      preference: Preferences.Profile.GetPatientSettings,
    };

    const response = await this.dataExchange<EncryptedPatientSettings>({
      siteUuid,
      payload,
      successKey: CommunicationKeys.PatientSettings.FetchSettingsSuccess,
      failureKey: CommunicationKeys.PatientSettings.FetchSettingsFailure,
    });

    if (!response?.data.encryptedSettings) {
      return {
        browser: {
          reminders: {
            mail: true,
          },
          instant: {
            mail: true,
          },
        },
        mobile: {
          reminders: {
            mail: true,
            fcm: true,
          },
          instant: {
            mail: true,
            fcm: true,
          },
        },
      };
    }

    const patientSettings =
      Encryption.decryptAndParseObject<DecryptedPatientSettings>(
        response.data.encryptedSettings,
        sharedSecretKey
      );

    if (!patientSettings) {
      throw new Error(
        `Failed to get patient settings siteId=${siteId} - No settings decrypted`
      );
    }
    if (typeof patientSettings.notificationSettings === 'string') {
      return JSON.parse(
        patientSettings.notificationSettings
      ) as PatientSettingsPayload;
    }

    return patientSettings.notificationSettings;
  }

  async updatePatientSettings(patientSettings: PatientSettingsPayload) {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { siteUuid } = getSiteByProjectId(this.config.projectId as string);

    const payload = {
      preference: Preferences.Profile.UpdatePatientSettings,
      notificationSettings: patientSettings,
    };

    return await this.dataExchange({
      siteUuid,
      payload,
      successKey: CommunicationKeys.PatientSettings.UpdateSettingsSuccess,
      failureKey: CommunicationKeys.PatientSettings.UpdateSettingsFailure,
    }).catch(error => {
      throw new Error(
        `Error with updating Patient settings data: ${JSON.stringify(error)}`
      );
    });
  }

  readonly subscribeToAccountReset = (callback: () => unknown) =>
    this.sourceService.subscribeToChild(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.AccountReset}`,
      snapshot => {
        if (!snapshot.exists()) return;
        callback();
      }
    );
}
